import React, { useMemo } from "react";
import {
  ERAS, TYPE_CATEGORIES, OTHER_TYPE, UNKNOWN_TYPE, typeCategory, shipYear, needsWork, needsWorkReasons,
} from "../helpers/shipFilters";
import { POINTS_PER_EDIT } from "../constants/siteConfig";

const MAX_HELP_LIST = 6;

// Floating filter card over the map (opened from the "Filters" map tool).
export default function FilterPanel({ ships, visibleCount, filters, bounds, onToggleType, onPatch, onReset, onShipClick, onOpenTimeline, onClose, active }) {
  // how many wrecks per type category, so empty categories can be hidden
  const typeCounts = useMemo(() => {
    const c = {};
    ships.forEach((s) => { const k = typeCategory(s.type); c[k] = (c[k] || 0) + 1; });
    return c;
  }, [ships]);

  const eraCounts = useMemo(() => {
    const c = {};
    ERAS.forEach((e) => { c[e.id] = ships.filter((s) => { const y = shipYear(s); return y !== null && (e.from === null || y >= e.from) && (e.to === null || y <= e.to); }).length; });
    return c;
  }, [ships]);

  const photoCount = useMemo(() => ships.filter((s) => s.images?.length > 0).length, [ships]);
  const needsList = useMemo(() => ships.filter(needsWork), [ships]);
  const undated = useMemo(() => ships.filter((s) => shipYear(s) === null).length, [ships]);

  const rangeSet = filters.from !== null || filters.to !== null;
  const eraActive = (e) => filters.from === e.from && filters.to === e.to;
  const pickEra = (e) => (eraActive(e) ? onPatch({ from: null, to: null }) : onPatch({ from: e.from, to: e.to }));

  const typeRows = [...TYPE_CATEGORIES, OTHER_TYPE, UNKNOWN_TYPE].filter((t) => typeCounts[t.id] || filters.types.includes(t.id));

  return (
    <aside className="filter-panel" role="dialog" aria-label="Filter wrecks">
      <header className="filter-head">
        <strong>Filters</strong>
        <div>
          <button type="button" className="filter-reset" onClick={onReset} disabled={!active}>Reset</button>
          <button type="button" className="filter-close" onClick={onClose} aria-label="Close filters">×</button>
        </div>
      </header>

      <div className="filter-body">
        <section>
          <h4>Type</h4>
          <div className="filter-chips">
            {typeRows.map((t) => (
              <button key={t.id} type="button" className={`filter-chip${filters.types.includes(t.id) ? " on" : ""}`} aria-pressed={filters.types.includes(t.id)} onClick={() => onToggleType(t.id)}>
                {t.label} <span>{typeCounts[t.id] || 0}</span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h4>Era / year of loss</h4>
          <div className="filter-chips">
            {ERAS.map((e) => (
              <button key={e.id} type="button" className={`filter-chip${eraActive(e) ? " on" : ""}`} aria-pressed={eraActive(e)} onClick={() => pickEra(e)} title={e.sub}>
                {e.label} <span>{eraCounts[e.id]}</span>
              </button>
            ))}
          </div>
          <div className="filter-row">
            <label>From <input type="number" inputMode="numeric" placeholder="any" value={filters.from ?? ""} onChange={(e) => onPatch({ from: e.target.value === "" ? null : Number(e.target.value) })} /></label>
            <label>To <input type="number" inputMode="numeric" placeholder="any" value={filters.to ?? ""} onChange={(e) => onPatch({ to: e.target.value === "" ? null : Number(e.target.value) })} /></label>
            {bounds && <button type="button" className="filter-link" onClick={onOpenTimeline}>Timeline ↗</button>}
          </div>
          {rangeSet && (
            <label className="filter-check">
              <input type="checkbox" checked={filters.includeUndated} onChange={(e) => onPatch({ includeUndated: e.target.checked })} />
              Include wrecks with no date ({undated})
            </label>
          )}
          {!rangeSet && undated > 0 && <p className="filter-note">{undated} wreck{undated === 1 ? " has" : "s have"} no date and are hidden whenever a year range is set.</p>}
        </section>

        <section>
          <h4>Photos &amp; quality</h4>
          <label className="filter-check">
            <input type="checkbox" checked={filters.hasPhotos} onChange={(e) => onPatch({ hasPhotos: e.target.checked })} />
            Has photos ({photoCount})
          </label>
          <label className="filter-check">
            <input type="checkbox" checked={filters.needsWork} onChange={(e) => onPatch({ needsWork: e.target.checked })} />
            Needs work: no photo or a short description ({needsList.length})
          </label>
          {filters.needsWork && (
            <div className="filter-help">
              <p>Open one and press <strong>✎ Suggest edit</strong>. Every approved edit earns {POINTS_PER_EDIT} points on the leaderboard.</p>
              <ul>
                {needsList.slice(0, MAX_HELP_LIST).map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => onShipClick(s)}>{s.title}</button>
                    <span>{needsWorkReasons(s).join(" · ")}</span>
                  </li>
                ))}
              </ul>
              {needsList.length > MAX_HELP_LIST && <p className="filter-note">+ {needsList.length - MAX_HELP_LIST} more on the map</p>}
            </div>
          )}
        </section>
      </div>

      <footer className="filter-foot">
        {active ? <>Showing <strong>{visibleCount}</strong> of {ships.length} wrecks</> : <>{ships.length} wrecks, no filters set</>}
        {active && visibleCount === 0 && <span className="filter-empty"> · nothing matches, try loosening a filter</span>}
      </footer>
    </aside>
  );
}
